export const WIZARD_STEPS = {
  DESTINATION: 1,
  DATES: 2,
  TRIP_TYPE: 3,
  INTERESTS_NEEDS: 4,
} as const;

export const WIZARD_STEP_NAMES = {
  // i18n translation keys, resolved where used
  [WIZARD_STEPS.DESTINATION]: 'destination.title',
  [WIZARD_STEPS.DATES]: 'wizard.dates.title',
  [WIZARD_STEPS.TRIP_TYPE]: 'tripType.title',
  [WIZARD_STEPS.INTERESTS_NEEDS]: 'interests.title',
} as const;

export const TOTAL_WIZARD_STEPS = 4;

export const MAX_TRIP_DAYS = 14;
export const GUEST_MAX_TRIP_DAYS = 14;

export const paths = {
  home: '/$locale',
  itinerary: '/$locale/itinerary',
  itineraryPreview: '/$locale/itinerary-preview',
  itineraries: '/$locale/itineraries',
  preitinerary: '/$locale/preitinerary',
  showcase: '/$locale/showcase',
  map: '/$locale/map',
  profile: '/$locale/profile',
  // Unified auth paths (localized)
  auth: '/$locale/auth/authorize',
  oauthCallback: '/$locale/auth/$provider/callback',
  // Other auth paths
  activate: '/$locale/auth/activate',
  verify: '/$locale/auth/verify',
  deletionStatus: '/$locale/auth/deletion-status',
} as const;

// OAuth Provider enum
export const OAUTH_PROVIDERS = {
  GOOGLE: 'google',
  SPOTIFY: 'spotify',
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
  TIDAL: 'tidal',
} as const;

export type OAuthProvider = keyof typeof OAUTH_PROVIDERS;
export type OAuthProviderValue =
  (typeof OAUTH_PROVIDERS)[keyof typeof OAUTH_PROVIDERS];

// Providers currently accepted by the unified OAuth API contract.
export const UNIFIED_OAUTH_PROVIDERS = {
  GOOGLE: OAUTH_PROVIDERS.GOOGLE,
  SPOTIFY: OAUTH_PROVIDERS.SPOTIFY,
  FACEBOOK: OAUTH_PROVIDERS.FACEBOOK,
  TIDAL: OAUTH_PROVIDERS.TIDAL,
} as const;

export type UnifiedOAuthProviderValue =
  (typeof UNIFIED_OAUTH_PROVIDERS)[keyof typeof UNIFIED_OAUTH_PROVIDERS];

export const isUnifiedOAuthProvider = (
  provider: OAuthProviderValue,
): provider is UnifiedOAuthProviderValue => {
  return Object.values(UNIFIED_OAUTH_PROVIDERS).includes(
    provider as UnifiedOAuthProviderValue,
  );
};

export const AFFILIATE_LINKS = {
  FLIGHTS: 'https://trip.tpo.lv/YJHy11Dq',
  HOTELS: 'https://www.agoda.com/partners/partnersearch.aspx?pcs=1&cid=1952335',
  TRANSPORT: 'https://gettransfer.tpo.lv/p8ST813L',
  INSURANCE: 'https://ektatraveling.tpo.lv/pxQRoxEK',
  TRANSFERS: 'https://kiwitaxi.tpo.lv/fbJeKsU8',
  CAR_RENTAL: 'https://qeeq.tpo.lv/Uy8CD3Ui',
  ESIM: 'https://mobimatter.com/#gohopsa',
  EVENT_TICKETS: 'https://ticketnetwork.tpo.lv/eZ884l7U',
  ATTRACTIONS_TICKETS: 'https://tiqets.tpo.lv/wA4midkN',
  GUIDED_TOURS: 'https://wegotrip.tpo.lv/V6OdY1mg',
  LUGGAGE_STORAGE: 'https://radicalstorage.tpo.lv/nqOFbjaq',
  BOAT_RENTAL: 'https://searadar.tpo.lv/6BWY6PyA',
} as const;

export const LEGAL_LINKS = {
  TERMS_AND_CONDITIONS: encodeURI(
    '/assets/pdf/ENG_terms.pdf'
  ),
} as const;

export const LEGAL_DOCUMENT_PATHS = {
  TERMS_AND_CONDITIONS: {
    en: encodeURI('/assets/pdf/ENG_terms.pdf'),
    pl: encodeURI('/assets/pdf/PL_terms.pdf'),
  },
  PRIVACY_POLICY: {
    en: 'https://hopsa.io/en/privacy-policy',
    pl: 'https://hopsa.io/pl/privacy-policy',
  },
} as const;

export const getTermsAndConditionsLink = (locale: string): string => {
  const normalizedLocale = locale.split('-')[0].toLowerCase();

  return normalizedLocale === 'pl'
    ? LEGAL_DOCUMENT_PATHS.TERMS_AND_CONDITIONS.pl
    : LEGAL_DOCUMENT_PATHS.TERMS_AND_CONDITIONS.en;
};

export const getPrivacyPolicyLink = (locale: string): string => {
  const normalizedLocale = locale.split('-')[0].toLowerCase();

  return normalizedLocale === 'pl'
    ? LEGAL_DOCUMENT_PATHS.PRIVACY_POLICY.pl
    : LEGAL_DOCUMENT_PATHS.PRIVACY_POLICY.en;
};

// OAuth Service enum
export const OAUTH_SERVICES = {
  LOGIN: 'login',
  REGISTER: 'register',
} as const;

export type OAuthService = keyof typeof OAUTH_SERVICES;
export type OAuthServiceValue =
  (typeof OAUTH_SERVICES)[keyof typeof OAUTH_SERVICES];

// OAuth Provider display names
export const OAUTH_PROVIDER_DISPLAY_NAMES = {
  [OAUTH_PROVIDERS.GOOGLE]: 'Google',
  [OAUTH_PROVIDERS.SPOTIFY]: 'Spotify',
  [OAUTH_PROVIDERS.FACEBOOK]: 'Facebook',
  [OAUTH_PROVIDERS.INSTAGRAM]: 'Instagram',
  [OAUTH_PROVIDERS.TIDAL]: 'Tidal',
} as const;

// Helper function to build paths with optional language prefix
export const buildPath = (path: string, locale?: 'en' | 'pl'): string => {
  if (!locale || locale === 'pl') return path; // Polish is default, no prefix
  if (path === '/') return `/${locale}`;
  return `/${locale}${path}`;
};

export const DEFAULT_FORM_DATA = {
  destination: undefined,
  tripType: undefined,
  interests: [],
  accessibilityNeeds: [],
  duration: 7,
  budget: 1000,
  dateRange: undefined,
  travelers: 1,
} as const;

export const MAX_DISPLAYED_DESTINATIONS = 3;

// Ticketmaster Events Configuration
export const TICKETMASTER_CONFIG = {
  DEFAULT_RADIUS: '50',
  DEFAULT_UNIT: 'km' as const,
  DIALOG_LIMIT: 100,
  LIMIT: 200,
  REPRESENTATIVE_LIMIT: 20,
} as const;

export const HOTELS_CONFIG = {
  DEFAULT_RADIUS: '10',
  DEFAULT_UNIT: 'km' as const,
  LIMIT: 100,
  REPRESENTATIVE_LIMIT: 20,
  DIALOG_LIMIT: 100,
} as const;

export const AGODA_CONFIG = {
  AFFILIATE_CID: '1952335',
  AFFILIATE_BASE_URL: 'https://www.agoda.com/en-gb/affiliate',
} as const;

export const TRANSPORT_MODE = {
  WALKING: 'walking',
  PUBLIC_TRANSPORT: 'public_transport',
  TAXI: 'taxi',
  RENTAL_CAR: 'rental_car',
  BICYCLE: 'bicycle',
} as const;

export type TransportMode =
  (typeof TRANSPORT_MODE)[keyof typeof TRANSPORT_MODE];

export const PLAN_STATUS = {
  AWAITING_PAYMENT: 'awaiting_payment',
  POI_SELECTION: 'poi_selection',
  AWAITING_CONFIRMATION: 'awaiting_confirmation',
  ITINERARY_GENERATION: 'itinerary_generation', // Phase 2: NEW 2-phase flow
  COMPLETED: 'completed',
  FAILED: 'failed',
  SAVED: 'saved',
} as const;

export type PlanStatus = (typeof PLAN_STATUS)[keyof typeof PLAN_STATUS];

// Generation scenarios
export const GENERATION_SCENARIO = {
  INITIAL: 'initial',
  OPTIMIZATION: 'optimization',
} as const;

export type GenerationScenario =
  (typeof GENERATION_SCENARIO)[keyof typeof GENERATION_SCENARIO];

export const MAIN_DOMAIN = 'GOHOPSA.COM';
export const MAIN_WEBSITE_URL = 'https://gohopsa.com';

export const getAttractionsBaseUrl = (locale: string = 'en'): string => {
  return `https://hopsa.io/${locale}/attractions`;
};
